import { useEffect, useRef } from "react";
import { EntitySaveCoordinator } from "./EntitySaveCoordinator";
import { coordinatorRegistry } from "./registry";
import type { CoordinatorConfig } from "./types";

/**
 * Creates and registers an EntitySaveCoordinator for the given entityId.
 * Call once per editing page (e.g., wizard root). Cleans up on unmount.
 *
 * The coordinator is registered eagerly during render so that child
 * components calling useEditor in the same render pass find it immediately.
 *
 * The save and onError callbacks are kept up-to-date via refs —
 * callers don't need to memoize them.
 */
export function useCreateEditor<
  TState extends Record<string, unknown>,
  TFields extends Record<string, unknown> = Partial<TState>,
>(entityId: string, config: CoordinatorConfig<TState, TFields>): void {
  const coordinatorRef = useRef<EntitySaveCoordinator<TState, TFields> | null>(
    null,
  );
  const registeredEntityId = useRef<string | null>(null);
  const configRef = useRef(config);
  configRef.current = config;

  function ensureCoordinator() {
    if (!coordinatorRef.current || registeredEntityId.current !== entityId) {
      coordinatorRef.current = new EntitySaveCoordinator({
        ...config,
        save: (fields) => configRef.current.save(fields),
        onError: (error) => configRef.current.onError?.(error),
      });
      coordinatorRegistry.register(entityId, coordinatorRef.current);
      registeredEntityId.current = entityId;
    }
  }

  // Eager creation during render: child useEditor hooks find the coordinator
  // in the same render pass without waiting for useEffect.
  ensureCoordinator();

  useEffect(() => {
    // Strict Mode recovery: cleanup destroyed the coordinator between
    // effect teardown and re-setup (no render in between). Recreate.
    ensureCoordinator();

    return () => {
      coordinatorRegistry.unregister(entityId);
      if (coordinatorRef.current) {
        coordinatorRef.current = null;
        registeredEntityId.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  useEffect(() => {
    if (coordinatorRef.current) {
      coordinatorRef.current.setServerState(config.initialState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.initialState]);
}
